<template>
  <OmtLayoutHeaderFooter
    :bg="['none', '#F6F6F9']"
    :header="{ titleLabel: titlePage }"
    :footer="!changePasswordRequired"
  >
    <OmtLayoutContainer
      class="scrollable-container"
      :alignContent="['', 'flex-start']"
    >
      <OmtRow mt="15">
        <OmtIcons
          size="2x"
          name="user-unlock"
          color="var(--colors-light-primary-navy-blue)"
        />
      </OmtRow>
      <OmtRow mt="8">
        <OmtTypography as="h1-regular">
          Sua senha
          <strong> expirou </strong>
        </OmtTypography>
      </OmtRow>
      <OmtRow
        mt="4"
        mb="8"
      >
        <OmtTypography as="h3-semi-bold">
          Por segurança, sua senha precisa ser atualizada. Crie uma nova senha para continuar acessando sua conta.
        </OmtTypography>
      </OmtRow>

      <OmtRow v-if="!changePasswordRequired">
        <PasswordInputField
          ref="currentPasswordField"
          label="Senha atual"
          :model="$v.currentPassword"
          :password="true"
          @focus="scrollToInput"
        />
      </OmtRow>

      <OmtRow
        v-if="!changePasswordRequired"
        class="error-continer"
      >
        <omt-p
          v-if="!$v.currentPassword.required && $v.currentPassword.$dirty"
          class="error"
        >
          &#8226; <span>Digite a senha atual</span>
        </omt-p>
      </OmtRow>

      <OmtRow>
        <PasswordInputField
          ref="newPasswordField"
          label="Senha"
          :model="$v.newPassword"
          :password="true"
          @focus="scrollToInput"
        />
      </OmtRow>

      <OmtRow>
        <PasswordInputField
          ref="againNewPasswordField"
          label="Repetir senha"
          :model="$v.againNewPassword"
          :password="true"
          @focus="scrollToInput"
        />
      </OmtRow>

      <OmtRow>
        <CBox>
          <OmtTypography
            as="h3-semi-bold"
            color="var(--colors-light-primary-gray2)"
            mb="16px"
          >
            Sua senha deve conter:
          </OmtTypography>
          <ValidationRuleItem
            :isValid="$v.newPassword.$model !== '' && $v.newPassword.minLength"
            rule="Pelo menos 8 caracteres."
          />
          <ValidationRuleItem
            :isValid="$v.newPassword.hasUpperCase"
            rule="Pelo menos 1 letra maiúscula (A-Z)."
          />
          <ValidationRuleItem
            :isValid="$v.newPassword.hasLowerCase"
            rule="Pelo menos 1 letra minuscula (a-z)."
          />
          <ValidationRuleItem
            :isValid="$v.newPassword.hasNumber"
            rule="No mínimo 1 número (0-9)."
          />
          <ValidationRuleItem
            :isValid="$v.newPassword.hasSpecialChars"
            rule="1 caractere especial (@#$%&)."
          />
        </CBox>
      </OmtRow>
      <FixedButtonRow :useFooter="!changePasswordRequired">
        <OmtButton
          :w="['100%', '308px !important']"
          :isDisabled="disabledButton"
          @click="showModal(true)"
        >
          {{ changePasswordRequired ? "Continuar" : "Alterar Senha" }}
        </OmtButton>
      </FixedButtonRow>
    </OmtLayoutContainer>
    <TokenModal
      v-if="showTokenModal"
      tokenKey="alteracao-senha"
      service="/change-password"
      @cancel="() => showModal(false)"
      @valid="validToken"
    />
    <ExpiredPasswordModal
      :isOpen.sync="successModalOpen"
      @continue="onConclude"
    />
  </OmtLayoutHeaderFooter>
</template>

<script>
import TokenModal from "@/components/Organisms/TokenModal";
import PasswordInputField from "@/components/Molecules/Password/PasswordInputField.vue";
import ValidationRuleItem from "@/components/Molecules/Password/ValidationRuleItem.vue";
import { mapActions, mapState } from "vuex";
import { required, sameAs, minLength } from "vuelidate/lib/validators";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import formValidations from "@/utils/formValidations";
import FixedButtonRow from "@/components/Atoms/FixedButtonRow/index.vue";
import ExpiredPasswordModal from "@/components/Organisms/ExpiredPassword/ExpiredPasswordModal.vue";
import { isMobile } from "@/utils/utils";

const { hasUpperCase, hasNumber, hasSpecialChars, hasLowerCase } = formValidations;

export default {
  name: "ChangePassword",
  components: {
    TokenModal,
    ExpiredPasswordModal,
    PasswordInputField,
    ValidationRuleItem,
    FixedButtonRow
  },
  data() {
    return {
      currentPassword: "",
      newPassword: "",
      againNewPassword: "",
      showTokenModal: false,
      forceChange: false,
      successModalOpen: false
    };
  },
  validations: {
    currentPassword: {
      required: function (_value) {
        return !this.changePasswordRequired;
      }
    },
    newPassword: {
      required,
      minLength: minLength(8),
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSpecialChars
    },
    againNewPassword: {
      required,
      sameAsPassword: sameAs("newPassword"),
      minLength: minLength(8),
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSpecialChars
    }
  },
  computed: {
    ...mapState("saude", ["userId", "currentPasswordCripto"]),
    ...mapState("myAccount", ["changePasswordRequired"]),
    titlePage() {
      return this.changePasswordRequired ? "Senha expirada" : "Troca de Senha";
    },
    disabledButton() {
      return (
        (!this.changePasswordRequired && !this.currentPassword) ||
        !this.newPassword ||
        !this.againNewPassword ||
        this.$v.$anyError ||
        (this.changePasswordRequired
          ? this.newPassword === this.currentPasswordCripto
          : this.newPassword === this.currentPassword)
      );
    }
  },
  watch: {
    changePasswordRequired: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.checkPasswordRequirements();
        }
      }
    }
  },
  mounted() {
    this.updateVh();
    globalThis.window.addEventListener("resize", this.updateVh);

    if (this.changePasswordRequired) {
      this.$root.$emit("hide-back-button");
    }
    this.$root.$emit("hide-loading");
    this.forceChange = this.$route.params.forceChange || this.changePasswordRequired;

    this.handleViewportChange = this.debounce(() => {
      this.adjustForMobileKeyboard();
    }, 100);

    globalThis.window.addEventListener("resize", this.handleViewportChange);
  },
  beforeDestroy() {
    globalThis.window.removeEventListener("resize", this.updateVh);
    if (this.handleViewportChange) {
      globalThis.window.removeEventListener("resize", this.handleViewportChange);
    }
  },
  methods: {
    updateVh() {
      document.documentElement.style.setProperty("--vh", `${globalThis.window.innerHeight * 0.01}px`);
    },
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
      };
    },
    adjustForMobileKeyboard() {
      const viewportHeightReduced = globalThis.window.innerHeight < globalThis.window.outerHeight * 0.8;

      if (isMobile() && viewportHeightReduced) {
        const activeElement = document.activeElement;
        if (activeElement && activeElement.tagName === "INPUT") {
          setTimeout(() => {
            this.scrollToElement(activeElement);
          }, 100);
        }
      }
    },
    ...mapActions("myAccount", ["changePassword"]),
    async onCancelClick() {
      await this.$router.replace({ name: "home" });
    },
    async logout() {
      this.$router.replace("/logout");
    },
    checkPasswordRequirements() {
      if (this.changePasswordRequired && !this.currentPasswordCripto) {
        this.logout();
      }
    },
    async onChangePwdClick({ auth }) {
      try {
        this.$root.$emit("show-loading");
        this.showModal(false);
        const resp = await this.changePassword({
          currentPassword: this.changePasswordRequired ? this.currentPasswordCripto : this.currentPassword,
          newPassword: this.newPassword,
          auth,
          isCurrentPasswordEncrypted: this.changePasswordRequired
        });
        this.$root.$emit("hide-loading");
        if (resp) {
          if (this.changePasswordRequired) {
            this.successModalOpen = true;
          } else {
            await Swal.fire({
              title: "Sucesso",
              text: "Sua senha foi alterada.",
              confirmButtonText: "OK"
            });
            await this.$router.replace({ name: "home" });
          }
        } else {
          await Swal.fire({
            title: "Aviso",
            text: "Senha informada não corresponde a senha atual.",
            confirmButtonText: "OK"
          });
        }
      } catch (error) {
        console.error(error);
        this.$root.$emit("hide-loading");
        await Swal.fire({
          title: "Aviso",
          text: "Sua senha não foi alterada. Por favor tente novamente mais tarde.",
          confirmButtonText: "OK"
        });
      }
    },

    showModal(value) {
      if (value === true) {
        this.formTouched = !this.$v.$anyDirty;
        this.errors = this.$v.$anyError;
        if (!this.errors && !this.formTouched) {
          this.showTokenModal = true;
        }
      } else {
        this.showTokenModal = false;
      }
    },
    async validToken({ auth }) {
      await this.onChangePwdClick({ auth });
    },
    onConclude() {
      this.successModalOpen = false;
      this.$router.replace({ name: "home" });
    },
    scrollToInput(event) {
      const input = event.target;
      if (input) {
        setTimeout(() => {
          this.scrollToElement(input);
        }, 100);
      }
    },
    scrollToElement(element) {
      if (!element) return;

      // Calcula a posição do elemento em relação ao viewport
      const rect = element.getBoundingClientRect();
      const isVisible = rect.top >= 0 && rect.bottom <= globalThis.window.innerHeight;

      // Se o elemento não estiver visível, faz scroll para ele
      if (!isVisible) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest"
        });
      }
    }
  }
};
</script>

<style scoped>
.scrollable-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 80px;
  min-height: calc(var(--vh, 1vh) * 100 - 80px);
}

@media (max-width: 768px) {
  .omt-layout-container {
    padding-bottom: 20px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    height: calc(100vh - 80px);
  }

  @media (max-height: 600px) {
    .omt-layout-container {
      padding-bottom: 40px;
    }
  }

  .omt-row {
    margin-bottom: 16px;
  }

  .omt-layout-header-footer {
    height: 100vh;
    overflow: hidden;
  }
}
</style>
